import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { DollarSign, Users, TrendingUp, Calendar, FileText, Download } from 'lucide-react';

const API_URL = "https://proprietario.mels.com.br/api";

const formatarDataBR = (data) => {
  if (!data) return '';
  try {
    const [ano, mes, dia] = data.split('-');
    if (ano && mes && dia) {
      return `${dia.padStart(2, '0')}/${mes.padStart(2, '0')}/${ano}`;
    }
    return data;
  } catch (error) {
    return '';
  }
};

export default function Relatorios({ academia, currentUser }) {
  const [tipoRelatorio, setTipoRelatorio] = useState('mensalidades');
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [datainicio, setDataInicio] = useState('');
  const [datafim, setDataFim] = useState('');
  const [totalizadores, setTotalizadores] = useState({
    total: 0,
    quantidade: 0
  });

  const isConsolidado = academia?.id === 'todas';

  useEffect(() => {
    const hoje = new Date();
    const primeiroDia = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
    setDataInicio(primeiroDia.toISOString().split('T')[0]);
    setDataFim(hoje.toISOString().split('T')[0]);
  }, []);

  useEffect(() => {
    if (datainicio && datafim && academia?.id) {
      const timer = setTimeout(() => {
        buscarRelatorio();
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [tipoRelatorio, datainicio, datafim, academia?.id]);

  const buscarRelatorio = async () => {
    if (!datainicio || !datafim || !academia?.id) return;
    
    try {
      setLoading(true);
      let endpoint = '';
      
      if (isConsolidado) {
        console.log('=== BUSCANDO RELATÓRIO CONSOLIDADO ===');
        console.log('Academia recebida:', academia);
        console.log('CurrentUser recebido:', currentUser);
        
        let academias = academia.academias || currentUser?.academias || [];
        
        console.log('Academias encontradas:', academias);
        console.log('Total de academias:', academias.length);
        
        if (academias.length === 0) {
          console.error(' ERRO: Nenhuma academia encontrada para consolidar');
          alert('Erro: Nenhuma academia encontrada. Por favor, recarregue a página.');
          setDados([]);
          setTotalizadores({ total: 0, quantidade: 0 });
          setLoading(false);
          return;
        }
        
        const ids = academias.map(a => a.id).join(',');
        console.log(' IDs para consolidar:', ids);
        
        if (!ids) {
          console.error('ERRO: Nenhum ID de academia encontrado');
          alert('Erro: IDs de academia não encontrados. Por favor, recarregue a página.');
          setDados([]);
          setTotalizadores({ total: 0, quantidade: 0 });
          setLoading(false);
          return;
        }
        
        switch(tipoRelatorio) {
          case 'mensalidades':
            endpoint = `/relatorio/mensalidades/todas?ids=${ids}`;
            break;
          case 'vendas':
            endpoint = `/relatorio/vendas/todas?ids=${ids}`;
            break;
          case 'avaliacoes':
            endpoint = `/relatorio/avaliacoes/todas?ids=${ids}`;
            break;
          case 'diarias':
            endpoint = `/relatorio/diarias/todas?ids=${ids}`;
            break;
          case 'totais':
            endpoint = `/relatorio/totais/todas?ids=${ids}`;
            break;
          case 'frequencia':
            endpoint = `/relatorio/frequencia/todas?ids=${ids}`;
            break;
          default:
            endpoint = `/relatorio/mensalidades/todas?ids=${ids}`;
        }
      } else {
        switch(tipoRelatorio) {
          case 'mensalidades':
            endpoint = `/relatorio/mensalidades/${academia.id}`;
            break;
          case 'vendas':
            endpoint = `/relatorio/vendas/${academia.id}`;
            break;
          case 'avaliacoes':
            endpoint = `/relatorio/avaliacoes/${academia.id}`;
            break;
          case 'diarias':
            endpoint = `/relatorio/diarias/${academia.id}`;
            break;
          case 'totais':
            endpoint = `/relatorio/totais/${academia.id}`;
            break;
          case 'frequencia':
            endpoint = `/relatorio/frequencia/${academia.id}`;
            break;
          default:
            endpoint = `/relatorio/mensalidades/${academia.id}`;
        }
      }

      const separador = endpoint.includes('?') ? '&' : '?';
      const url = `${API_URL}${endpoint}${separador}datainicio=${datainicio}&datafim=${datafim}`;
      console.log(' Buscando relatório:', url);
      
      const response = await fetch(url);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Erro na resposta:', errorText);
        throw new Error(`Erro HTTP: ${response.status} - ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Dados recebidos:', data);
      console.log('Tipo de dados:', typeof data);
      console.log('É array?', Array.isArray(data));

      let dadosArray = [];
      
      if (Array.isArray(data)) {
        dadosArray = data;
      } else if (data?.dados && Array.isArray(data.dados)) {
        dadosArray = data.dados;
      } else if (data?.registros && Array.isArray(data.registros)) {
        dadosArray = data.registros;
      } else if (data && typeof data === 'object') {
        const possiveisChaves = Object.keys(data);
        for (const chave of possiveisChaves) {
          if (Array.isArray(data[chave])) {
            dadosArray = data[chave];
            break;
          }
        }
      }

      console.log('Array final de dados:', dadosArray.length, 'registros');
      setDados(dadosArray);

      if (tipoRelatorio !== 'frequencia') {
        const total = dadosArray.reduce((sum, item) => sum + (parseFloat(item.valor) || 0), 0);
        console.log('Total calculado:', total);
        setTotalizadores({
          total: total,
          quantidade: dadosArray.length
        });
      } else {
        setTotalizadores({
          total: 0,
          quantidade: dadosArray.length
        });
      }

    } catch (error) {
      console.error('Erro ao buscar relatório:', error);
      
      if (error.message.includes('Failed to fetch')) {
        alert('Erro de conexão: Verifique se o backend está rodando e se o CORS está configurado corretamente.');
      } else if (error.message.includes('503')) {
        alert('Servidor temporariamente indisponível. O backend pode estar "acordando" (Render free tier). Tente novamente em alguns segundos.');
      } else {
        alert(`Erro ao buscar relatório: ${error.message}`);
      }
      
      setDados([]);
      setTotalizadores({ total: 0, quantidade: 0 });
    } finally {
      setLoading(false);
    }
  };

  const exportarCSV = () => {
    if (dados.length === 0) {
      alert('Não há dados para exportar');
      return;
    }

    let csvContent = '';
    
    if (tipoRelatorio === 'frequencia') {
      csvContent = 'Data,Hora,Cliente,Tipo Acesso,Motivo\n';
      dados.forEach(item => {
        csvContent += `${item.data},${item.hora},${item.cliente},${item.tipo_acesso},${item.motivo || ''}\n`;
      });
    } else {
      csvContent = 'Data,Hora,Cliente,Valor,Atividade,Forma Pgto,Tipo Cliente,Funcionário\n';
      dados.forEach(item => {
        csvContent += `${item.data},${item.hora},${item.cliente},${item.valor},${item.atividade},${item.forma_pgto},${item.tipo_cliente},${item.funcionario}\n`;
      });
    }

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    const nomeArquivo = isConsolidado 
      ? `relatorio_consolidado_${tipoRelatorio}_${datainicio}_${datafim}.csv`
      : `relatorio_${tipoRelatorio}_${datainicio}_${datafim}.csv`;
    link.setAttribute('download', nomeArquivo);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatarValor = (valor) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  };

  const formatarData = (data) => {
    if (!data) return '';
    try {
      if (typeof data === 'number') {
        const d = new Date(data);
        return d.toLocaleDateString('pt-BR');
      }
      
      if (typeof data === 'string' && data.includes('T')) {
        const d = new Date(data);
        return d.toLocaleDateString('pt-BR');
      }
      
      if (typeof data === 'string' && data.includes('-')) {
        const [ano, mes, dia] = data.split('-');
        if (ano && mes && dia) {
          return `${dia.padStart(2, '0')}/${mes.padStart(2, '0')}/${ano}`;
        }
      }
      
      if (typeof data === 'string' && data.includes('/')) {
        return data;
      }
      
      const d = new Date(data);
      if (!isNaN(d.getTime())) {
        return d.toLocaleDateString('pt-BR');
      }
      
      return '';
    } catch (error) {
      console.error('Erro ao formatar data:', error);
      return '';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Cabeçalho - RESPONSIVO */}
        <div className="bg-white shadow-sm border-b border-gray-200 px-6 py-4 mb-6 rounded-lg">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{academia?.nome}</h1>
              {isConsolidado && (
                <p className="text-sm text-gray-600 mt-1">
                  Visualização consolidada de todas as academias
                </p>
              )}
            </div>
            <button
              onClick={exportarCSV}
              disabled={dados.length === 0}
              className="flex items-center justify-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed w-full md:w-auto"
            >
              <Download className="w-4 h-4" />
              <span>Exportar CSV</span>
            </button>
          </div>
        </div>

        {/* Filtros - RESPONSIVO */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Tipo de Relatório */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo de Relatório
              </label>
              <select
                value={tipoRelatorio}
                onChange={(e) => setTipoRelatorio(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="mensalidades">Mensalidades</option>
                <option value="vendas">Vendas</option>
                <option value="avaliacoes">Avaliações Físicas</option>
                <option value="diarias">Diárias</option>
                <option value="totais">Recebimentos Totais</option>
                <option value="frequencia">Frequência</option>
              </select>
            </div>

            {/* Data Início */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Data Início
              </label>
              <input
                type="date"
                value={datainicio}
                onChange={(e) => setDataInicio(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>

            {/* Data Fim */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Data Fim
              </label>
              <input
                type="date"
                value={datafim}
                onChange={(e) => setDataFim(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Totalizadores - RESPONSIVO */}
        {tipoRelatorio !== 'frequencia' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-md p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-sm">
                    Valor Total {isConsolidado && '(Consolidado)'}
                  </p>
                  <p className="text-3xl font-bold mt-2">
                    {formatarValor(totalizadores.total)}
                  </p>
                </div>
                <DollarSign className="w-12 h-12 text-green-200" />
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-md p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm">
                    Total de Registros {isConsolidado && '(Consolidado)'}
                  </p>
                  <p className="text-3xl font-bold mt-2">{totalizadores.quantidade}</p>
                </div>
                <FileText className="w-12 h-12 text-blue-200" />
              </div>
            </div>
          </div>
        )}

        {/* Tabela de Dados - RESPONSIVO */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">
            {tipoRelatorio === 'mensalidades' && `Recebimentos de Mensalidades${isConsolidado ? ' (Todas as Academias)' : ''}`}
            {tipoRelatorio === 'vendas' && `Recebimentos de Vendas${isConsolidado ? ' (Todas as Academias)' : ''}`}
            {tipoRelatorio === 'avaliacoes' && `Recebimentos de Avaliações Físicas${isConsolidado ? ' (Todas as Academias)' : ''}`}
            {tipoRelatorio === 'diarias' && `Recebimentos de Diárias${isConsolidado ? ' (Todas as Academias)' : ''}`}
            {tipoRelatorio === 'totais' && `Todos os Recebimentos${isConsolidado ? ' (Todas as Academias)' : ''}`}
            {tipoRelatorio === 'frequencia' && `Registro de Frequência${isConsolidado ? ' (Todas as Academias)' : ''}`}
          </h2>

          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
              <p className="text-gray-600 mt-2">Carregando...</p>
            </div>
          ) : dados.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              Nenhum registro encontrado no período selecionado
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Data</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Hora</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Cliente</th>
                    {tipoRelatorio !== 'frequencia' && (
                      <>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Valor</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Atividade</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Forma Pgto</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Tipo</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Funcionário</th>
                      </>
                    )}
                    {tipoRelatorio === 'frequencia' && (
                      <>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Tipo Acesso</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Motivo</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {dados.map((item, index) => (
                    <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm text-gray-900">
                        {formatarData(item.data)}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-700">{item.hora}</td>
                      <td className="py-3 px-4 text-sm text-gray-900 font-medium">{item.cliente}</td>
                      {tipoRelatorio !== 'frequencia' && (
                        <>
                          <td className="py-3 px-4 text-sm font-semibold text-green-600">
                            {formatarValor(item.valor)}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-700">{item.atividade}</td>
                          <td className="py-3 px-4 text-sm text-gray-700">{item.forma_pgto}</td>
                          <td className="py-3 px-4">
                            <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                              item.tipo_cliente === 'CLIENTE NOVO' 
                                ? 'bg-blue-100 text-blue-800'
                                : item.tipo_cliente === 'RENOVAÇÃO'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {item.tipo_cliente}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-700">{item.funcionario}</td>
                        </>
                      )}
                      {tipoRelatorio === 'frequencia' && (
                        <>
                          <td className="py-3 px-4 text-sm text-gray-700">{item.tipo_acesso}</td>
                          <td className="py-3 px-4 text-sm text-gray-700">{item.motivo || '-'}</td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}